import React from "react";
import { useParams } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { Calendar, ArrowLeft } from "lucide-react";

// 静的なお知らせデータ
const newsData: Record<string, {
  id: number;
  title: string;
  date: string;
  category: string;
  badge: string;
  badgeColor: string;
  content: string;
}> = {
  "joybase-beta-release": {
    id: 1,
    title: "JOYBASEベータ版がリリースされました",
    date: "2025/10/31",
    category: "リリース",
    badge: "NEW",
    badgeColor: "bg-red-100 text-red-800",
    content: `
      <p class="mb-4">この度、JOYBASEのベータ版をリリースいたしました。</p>

      <h2 class="text-xl font-bold text-gray-900 mb-3 mt-6">JOYBASEとは</h2>
      <p class="mb-4">
        JOYBASEは、カードゲーム店舗様向けのオリジナルパック（オリパ）販売プラットフォームです。
        初期費用0円、月額費用0円で、誰でも簡単にオリパサイトを始めることができます。
      </p>

      <h2 class="text-xl font-bold text-gray-900 mb-3 mt-6">主な機能</h2>
      <ul class="list-disc list-inside mb-4 space-y-2">
        <li>簡単なイベント作成・管理機能</li>
        <li>自動抽選システム</li>
        <li>配送管理機能</li>
        <li>売上管理・分配システム</li>
        <li>カスタマイズ可能なストアデザイン</li>
      </ul>

      <h2 class="text-xl font-bold text-gray-900 mb-3 mt-6">ベータ版について</h2>
      <p class="mb-4">
        現在、ベータ版として運用しており、機能の追加や改善を継続的に行っております。
        ご利用いただく中でお気づきの点がございましたら、お気軽にお問い合わせください。
      </p>

      <p class="mb-4">
        皆様のご意見を参考に、より使いやすいサービスを目指してまいります。
      </p>
    `
  }
};

const NewsDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const news = slug ? newsData[slug] : null;

  if (!news) {
    return (
      <>
        <Helmet>
          <title>お知らせが見つかりません</title>
        </Helmet>
        <div className="min-h-screen bg-theme-bg">
          <div className="pt-20">
            <div className="max-w-screen-lg mx-auto px-4 py-8">
              <div className="bg-white border-theme rounded-theme-lg shadow-theme p-8 sm:p-12 text-center">
                <h1 className="text-2xl [font-weight:var(--theme-font-weight)] text-black mb-4">お知らせが見つかりません</h1>
                <p className="[font-weight:var(--theme-font-weight-button)] text-gray-600 mb-6">お探しのお知らせは存在しないか、削除された可能性があります。</p>
                <a
                  href="/news"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-theme-primary border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-theme-button shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                >
                  <ArrowLeft size={20} />
                  お知らせ一覧に戻る
                </a>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{news.title}</title>
        <meta name="description" content={news.title} />
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <div className="pt-20">
          <div className="max-w-screen-lg mx-auto px-4 py-8">
            {/* 戻るボタン */}
            <a
              href="/news"
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] text-sm hover:bg-gray-100 transition-colors mb-6"
            >
              <ArrowLeft size={16} />
              お知らせ一覧に戻る
            </a>

            {/* 記事 */}
            <article className="bg-white border-theme rounded-theme-lg shadow-theme p-4 sm:p-8">
              {/* ヘッダー */}
              <div className="mb-6 pb-6 border-b-[length:var(--theme-border-width)] border-[var(--theme-border-color)]">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-block px-3 py-1 bg-red-400 border-theme text-black text-xs [font-weight:var(--theme-font-weight)] rounded-theme">
                    {news.badge}
                  </span>
                  <span className="px-2 py-1 bg-gray-100 border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)]">{news.category}</span>
                </div>

                <h1 className="text-2xl sm:text-3xl [font-weight:var(--theme-font-weight)] text-black mb-4">{news.title}</h1>

                <div className="flex items-center gap-2 [font-weight:var(--theme-font-weight-button)] text-gray-600">
                  <Calendar size={16} />
                  <time>{news.date}</time>
                </div>
              </div>

              {/* 本文 */}
              <div
                className="prose prose-gray max-w-none prose-headings:[font-weight:var(--theme-font-weight)] prose-headings:text-black prose-p:font-medium prose-li:font-medium"
                dangerouslySetInnerHTML={{ __html: news.content }}
              />
            </article>

            {/* 戻るボタン（下部） */}
            <div className="mt-8 text-center">
              <a
                href="/news"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-black shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
              >
                <ArrowLeft size={20} />
                お知らせ一覧に戻る
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewsDetailPage;
